import { useEffect, useState } from 'react';
import { useActionsContext } from './context';
import { Model, ModelConstructor, TargetableModel } from './types';

type PropertyKeys<T> = {
  [K in keyof T]: T[K] extends Function ? never : K;
}[keyof T];

export type PropertiesOnly<T> = Pick<T, PropertyKeys<T>>;

export const useInScopeModel = <T extends Model>(
  modelClass: ModelConstructor<T>,
  modelId?: string
): T => {
  const { getInScopeModelOfType: getModelOfType } = useActionsContext();

  return getModelOfType<T>(modelClass, modelId) as T;
};

export const useTargetedModel = <T extends TargetableModel>(
  modelClass: ModelConstructor<T>
): T | null => {
  const context = useActionsContext();
  const [, setTick] = useState(0);

  useEffect(() => {
    return context.subscribe(() => setTick((t) => t + 1));
  }, [context]);

  return context.targetedModelOfType(modelClass) ?? null;
};

export const useStatefullyAwareInScopeModel = <T extends Model>(
  modelClass: ModelConstructor<T>,
  modelId?: string
): T | null => {
  const context = useActionsContext();
  const [, setTick] = useState(0);

  useEffect(() => {
    return context.subscribe(() => setTick((t) => t + 1));
  }, [context]);

  return context.getInScopeModelOfType(modelClass, modelId) ?? null;
};
